import { useCallback, useEffect, useRef, useState } from "react";
import type { RefCallback } from "react";
import * as amplitude from "@amplitude/analytics-browser";

type UseSectionInViewOptions = {
  section: string;
};

const VISIBILITY_THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1.0];
const TARGET_RATIO = 0.5;

export function useSectionInView<T extends HTMLElement = HTMLDivElement>({
  section,
}: UseSectionInViewOptions) {
  const [node, setNode] = useState<T | null>(null);
  const firedRef = useRef(false);
  const activeObserverRef = useRef<IntersectionObserver | null>(null);
  const sectionRef = useRef(section);

  sectionRef.current = section;

  const setRef = useCallback<RefCallback<T>>((nextNode) => {
    setNode((currentNode) => (currentNode === nextNode ? currentNode : nextNode));
  }, []);

  useEffect(() => {
    if (!node || firedRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver((entries, currentObserver) => {
      if (activeObserverRef.current !== currentObserver) {
        return;
      }

      for (const entry of entries) {
        if (firedRef.current) break;
        if (!entry.isIntersecting) continue;

        // Literal 50%: fires when >=50% of the element is in the viewport.
        // Fallback for elements taller than ~2x viewport (which can never reach 50%
        // intersection): fire when the element is as visible as it can physically be.
        const rootH = entry.rootBounds?.height ?? 0;
        const elH = entry.boundingClientRect.height;
        const maxPossible = rootH > 0 && elH > 0 ? Math.min(1, rootH / elH) : 1;
        const effectiveTarget = Math.min(TARGET_RATIO, maxPossible * 0.95);

        if (entry.intersectionRatio < effectiveTarget) continue;

        firedRef.current = true;
        amplitude.track("pricing_section_viewed", {
          section: sectionRef.current,
          page_height_px: document.documentElement.scrollHeight,
        });
        currentObserver.disconnect();
        activeObserverRef.current = null;
        break;
      }
    }, { threshold: VISIBILITY_THRESHOLDS });

    activeObserverRef.current = observer;
    observer.observe(node);

    return () => {
      if (activeObserverRef.current === observer) {
        activeObserverRef.current = null;
      }

      observer.disconnect();
    };
  }, [node]);

  return setRef;
}

export function trackPricingCtaClicked(location: string) {
  amplitude.track("pricing_cta_clicked", { location });
}
